import { MutationFunctionOptions } from '@apollo/react-common';
import { Action } from '../../Card';
import { Content, Footer, Header, TripleAction } from '../../../libs/gs-component-right-rail';
import { InlineAlert } from '@xo-union/tk-component-alerts';
import Spinner from '@xo-union/tk-component-spinner';
import Honeybadger from 'honeybadger-js';
import pluralize from 'pluralize';
import React, { useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';

import { PhotoUploading } from '.';
import {
  PhotoTimelineItemsFragment,
  UpdatePhotoTimelineItemMutation,
  UpdatePhotoTimelineItemMutationVariables,
  useCreatePhotoTimelineItemMutation,
  useWeddingWebsiteQuery,
  Wws_PageItem,
} from '../../../generated/graphql';
import addItemToPage from '../../../helpers/apolloCache/addItemToPage';
import updatePageItem from '../../../helpers/apolloCache/updatePageItem';
import { previewRoute } from '../../../helpers/routes';
import { useManageItem } from '../../../helpers/useManageItem';
import { Remover } from '../../FormComponents';
import styles from './upload.scss';
import UploadsList from './UploadsList';
import { pagesQuery } from '../../LeftContainer/graphql';
import { imageFileTypesWithoutGif } from '../ImageField';

pluralize.addIrregularRule('it', 'them');

interface PhotoTimelineUploadProps {
  addUploadsOnChange(event: React.ChangeEvent<HTMLInputElement>): void;
  id: number | null;
  pageId: number;
  timelineId?: number;
  photosUploading: PhotoUploading[];
  setPhotosUploading: Function;
  update(
    options?: MutationFunctionOptions<UpdatePhotoTimelineItemMutation, UpdatePhotoTimelineItemMutationVariables>
  ): Promise<any>;
  updateLoading: boolean;
  timelineLoading: boolean;
  uploadsWithErrors: PhotoUploading[];
  onDelete: () => void;
  trackItem(pageItem?: Wws_PageItem): void;
}

const PhotoTimelineUpload = (props: PhotoTimelineUploadProps) => {
  const [create, { loading: createLoading }] = useCreatePhotoTimelineItemMutation();
  const history = useHistory();
  const { close } = useManageItem();
  const [isErroring, setIsErroring] = useState(false);
  const { pathname } = useLocation();
  const { data: weddingData } = useWeddingWebsiteQuery();
  const {
    addUploadsOnChange,
    id,
    pageId,
    timelineId,
    photosUploading,
    setPhotosUploading,
    update,
    updateLoading,
    uploadsWithErrors,
    timelineLoading,
    onDelete,
    trackItem,
  } = props;

  const photosUploaded = photosUploading.filter(
    photoUploading => photoUploading.mediaResponse && !photoUploading.error
  );

  const isUploading = !!photosUploading.filter(
    upload => upload.percentUploaded && upload.percentUploaded < 100 && !upload.error
  ).length;
  const isSaving = createLoading || updateLoading;
  const isSavingOrNoPhotosUploaded = isUploading || isSaving || isErroring || photosUploaded.length === 0;
  const uploadsErrorCount = uploadsWithErrors.length;
  const isPublished = weddingData?.weddingWebsite?.state === 'published';

  const closeRail = () => {
    if (pathname.includes('/preview')) {
      history.push(previewRoute);
    } else {
      history.push('/gs/dashboard');
    }
  };

  const saveUploads = async () => {
    const newPhotoItems = photosUploaded.map(({ id, mediaResponse, mediaMetadata }) => ({
      id,
      pageId,
      mediaApiId: mediaResponse?.mediaApiId || '',
      date: mediaMetadata?.date,
      caption: mediaMetadata?.caption,
      height: mediaMetadata?.height || mediaResponse?.height || 0,
      width: mediaMetadata?.width || mediaResponse?.width || 0,
      cropX: mediaMetadata?.x,
      cropY: mediaMetadata?.y,
      rotation: mediaMetadata?.rotation,
    }));

    try {
      if (id) {
        await update({
          variables: {
            attributes: {
              pageId,
              photoItems: newPhotoItems,
              id,
            },
          },
          update(cache, { data }) {
            const item = data?.item;
            if (item) {
              updatePageItem<PhotoTimelineItemsFragment | undefined>(cache, pageId, id, item);
              trackItem(item);
              close(item);
            }
          },
        });
      } else {
        await create({
          variables: {
            attributes: {
              pageId,
              photoItems: newPhotoItems,
            },
          },
          update(cache, { data }) {
            const item = data?.item;
            if (item) {
              addItemToPage<PhotoTimelineItemsFragment>(cache, pageId, item);
              trackItem(item);
              close(item);
            }
          },
          refetchQueries: [{ query: pagesQuery }],
        });
      }
      closeRail();
    } catch (error) {
      Honeybadger.notify(error, 'Error Saving Uploaded Photos');
    }
  };

  return (
    <>
      <Header leftButtonText=" " leftButtonOnClick={history.goBack} as="h1">
        Add Photo Memory
      </Header>
      <Content>
        {timelineLoading ? (
          <div className={styles.loading}>
            <Spinner />
          </div>
        ) : (
          <>
            <label className={styles.addMorePhotos}>
              <Action iconName="add_circle" onClick={() => {}} data-testid="add-more-photos">
                <input
                  type="file"
                  data-testid="photo-timeline-add-more"
                  multiple
                  accept={imageFileTypesWithoutGif.join(',')}
                  onChange={addUploadsOnChange}
                  style={{ display: 'none' }}
                />
                Add More Memories
              </Action>
            </label>
            {uploadsErrorCount > 0 && (
              <InlineAlert type="info" className={styles.uploadErrorBanner}>
                {`Oops! It looks like ${uploadsErrorCount} ${pluralize('photo', uploadsErrorCount)} didn't upload. `}
                <strong>{`Please try ${pluralize('it', uploadsErrorCount)} again.`}</strong>
              </InlineAlert>
            )}
            <UploadsList
              uploads={photosUploading}
              onChange={uploads => {
                setIsErroring(false);
                setPhotosUploading(uploads);
              }}
              onError={() => setIsErroring(true)}
              pageId={pageId}
              timelineId={timelineId}
            />
            <div className={styles.removerContainer}>
              <Remover onClick={onDelete}>Delete timeline</Remover>
            </div>
          </>
        )}
      </Content>
      <Footer>
        <TripleAction
          secondaryText="Cancel"
          secondaryOnClick={closeRail}
          secondaryDisabled={isSaving}
          primaryText={isPublished ? 'Save and Publish' : 'Save'}
          primaryOnClick={saveUploads}
          primaryDisabled={isSavingOrNoPhotosUploaded}
        />
      </Footer>
    </>
  );
};

export default PhotoTimelineUpload;
